import { Flex } from "antd";
import { arePrimaryAttrsFilled } from "features/cases/utils";
import { useOnboarding } from "features/onboarding";
import { useDataQuery } from "hooks/useData";
import { useIsMobile } from "hooks/useIsMobile";
import { useScrollTo } from "hooks/useScrollTo";
import { useTexts } from "hooks/useTexts";
import { useUser } from "hooks/useUser";
import React from "react";
import { useRef } from "react";
import { useParams } from "react-router";
import arrowLong from "static/icons/arrow-long.svg";
import onboardingIcon from "static/icons/onboarding.svg";
import { Attribute } from "types/api-types";
import BubbleButton from "ui/BubbleButton";
import LoadingPlaceholder from "ui/LoadingPlaceholder";

type OwnProps = {
  kind?: "problem" | "solution";
  expanded?: boolean;
  setExpanded?: any;
  expanded2?: boolean;
  setExpanded2?: any;
  problemId?: string;
  solutionId?: string;
  setSolsExpanded?: any;
  setProbExpanded?: (expanded: boolean) => void;
  probExpanded?: boolean;
  setCaseExpanded?: (expanded: boolean) => void;
  caseExpanded?: boolean;

  isVisible?: boolean;
  onToggle?: (val?: boolean) => void;
};

const AttrBar: React.FC<OwnProps> = ({
  kind = "problem",
  setSolsExpanded,
  setProbExpanded,
  isVisible = true,
  onToggle,
  setCaseExpanded,
}) => {
  const { isMobile } = useIsMobile();
  const { txt } = useTexts("attr_bar");

  const ref0 = useRef(null);
  const ref1 = useRef(null);
  const ref2 = useRef(null);
  const ref3 = useRef(null);

  const refs = [ref0, ref1, ref2, ref3];
  const { isEditor, isLoggedIn } = useUser();

  const { problemId } = useParams<{ problemId: string }>();
  const { solutionId } = useParams<{ solutionId: string }>();

  const problemAttrs = useDataQuery<Attribute[]>("attrs/?attributeOf=problem");
  const solutionAttrs = useDataQuery<Attribute[]>(
    "attrs/?attributeOf=solution",
  );
  const attrs = kind === "problem" ? problemAttrs : solutionAttrs;

  const cardsProblem = useDataQuery(
    `problems/${problemId}/cards/`,
    !problemId,
    [],
  );

  const problem = useDataQuery(`problems/${problemId}`, !problemId, {});

  const solutionCount = problem?.solutionCount || 0;

  const cardsSolution: any[] = useDataQuery(
    `problems/${problemId}/solutions/${solutionId}/cards/`,
    !solutionId || !problemId,
    [],
  );

  const cards: any[] = kind === "problem" ? cardsProblem : cardsSolution;

  const { scrollTo } = useScrollTo();
  const setTab = (tab: string, solsExpand = false) => {
    if (isMobile) onToggle?.(false);

    scrollTo(tab);
    setSolsExpanded?.(solsExpand);
    setProbExpanded?.(!solsExpand);
  };

  const setAttrTab = (attrId: number) => {
    if (isMobile) onToggle?.(false);

    const slug = attrs?.find((attr) => attr.id === attrId)?.slug;
    scrollTo(slug ?? `attribute-${attrId}`);
  };

  const setToTop = () => setTab("topcard");
  const setToSecond = () => {
    setTab("secondcard", kind === "problem");
  };

  const {
    tourNode: onboardingBlock,
    hasSteps: hasOnboarding,
    openTour,
  } = useOnboarding({
    topic: "reading",
    refs,
    condition: isLoggedIn && kind === "problem" && !!cards?.length,
    delay: 2000,
    onOpen: () => {
      if (isMobile) onToggle?.(true);
    },
    onClose: () => {
      if (isMobile) onToggle?.(false);
    },
  });

  if (!attrs) return <LoadingPlaceholder />;

  if (!cards) return <LoadingPlaceholder />;

  const primaryAttrsFilled = arePrimaryAttrsFilled(attrs, cards);

  const attrList = isVisible ? (
    <div
      ref={ref2}
      className={
        isMobile
          ? isVisible
            ? "attr-bar-mobile"
            : "attr-bar-mobile attr-bar-mobile-hidden"
          : "attr-bar"
      }
    >
      {attrs.map((attr, index, array) => {
        const hasCards =
          cards.filter((card) => {
            return card.attr === attr.id || card.attributeType === attr.id;
          }).length > 0;

        const disabled = isEditor
          ? !hasCards && !primaryAttrsFilled && !attr.isPrimary
          : !hasCards;

        const overrideBackgroundColor =
          !hasCards && isEditor && !disabled ? "#" + attr.lightColor : null;

        const color = hasCards ? "#" + attr.color : undefined;

        return (
          <BubbleButton
            group={true}
            suffix="-new"
            borderColor={"#666"}
            text={attr.title}
            key={attr.id}
            disabled={disabled}
            backgroundColor={overrideBackgroundColor || color || "#c0c0c0"}
            onClick={() => setAttrTab(attr.id)}
            round={
              index === 0
                ? "left"
                : index === array.length - 1
                  ? "right"
                  : "none"
            }
          />
        );
      })}
    </div>
  ) : null;

  return (
    <div className={isMobile ? "main-menu-mobile" : "main-menu"}>
      <Flex
        align="start"
        gap="18px"
        justify={"space-between"}
        style={isMobile ? { width: "100%" } : {}}
      >
        <div className="main-menu-left-section">
          <div className="main-menu-left-section-button">
            <span className="arrow-long-container">
              <img
                src={arrowLong}
                alt=""
              />
            </span>
            <BubbleButton
              ref={ref1}
              group={true}
              suffix="-new"
              backgroundColor={kind === "problem" ? "#FCD757" : "#22AAE0"}
              onClick={kind === "problem" ? setToTop : setToSecond}
              text={
                kind === "problem"
                  ? txt("problem", "Проблема")
                  : txt("solution", "Решение")
              }
            />
          </div>
          {isMobile ? null : attrList}
        </div>

        <div className="main-menu-right-section">
          {kind === "problem" && (
            <div>
              {/*@ts-ignore*/}
              {!!solutionCount && (
                <BubbleButton
                  ref={ref3}
                  group={true}
                  suffix="-new"
                  backgroundColor={kind === "problem" ? "#22AAE0" : "#FCD757"}
                  onClick={kind === "problem" ? setToSecond : setToTop}
                  text={
                    kind === "problem"
                      ? txt("solution", "Решения")
                      : txt("problem", "Проблема")
                  }
                />
              )}
            </div>
          )}

          {kind === "solution" && (
            <div>
              <BubbleButton
                ref={ref3}
                group={true}
                suffix="-new"
                backgroundColor={"#FCD757"}
                // @ts-ignore
                onClick={() => setCaseExpanded?.(true)}
                text={txt("problem", "Проблема")}
              />
            </div>
          )}

          {isMobile && (
            <BubbleButton
              backgroundColor={"#c0c0c0"}
              onClick={() => {
                onToggle?.();
              }}
              text={"☰"}
              variant="circle"
            />
          )}

          {!isMobile && hasOnboarding && (
            <BubbleButton
              ref={ref0}
              variant="circle"
              text={""}
              icon={onboardingIcon}
              onClick={() => openTour()}
            />
          )}
        </div>
      </Flex>

      {!isMobile && onboardingBlock}

      <div>{isMobile && isVisible ? attrList : null}</div>
    </div>
  );
};

export default AttrBar;
